import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/home.css';
import { fetchUserLanguagePreference, saveUserLanguagePreference } from '../utils/api';
import { fetchGdprOrganizations, fetchGdprRoles } from '../utils/gdprApi';
import {
  normalizeGdprOrganizations,
  normalizeGdprRoles,
  saveOrganizationRoleSelection
} from '../utils/orgRoleSelection';
import {
  LANGUAGE_OPTIONS,
  getLanguageByCode,
  getLanguageCodeFromPreferenceValue,
  isSupportedLanguage,
  useTranslation
} from '../utils/i18n';

const getStoredJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getFirstString = (...values) => (
  values.find((value) => typeof value === 'string' && value.trim())?.trim() || ''
);

const getInitials = (name, email) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return String(email || 'U').slice(0, 2).toUpperCase();
};

const buildProfile = () => {
  const dhtUser = getStoredJson('dhtUser') || {};
  const authUser = getStoredJson('authUser') || {};

  const firstName = getFirstString(
    dhtUser.GDPRUsr_FirstName,
    dhtUser.firstName,
    dhtUser.given_name,
    authUser.firstName
  );
  const lastName = getFirstString(
    dhtUser.GDPRUsr_SurName,
    dhtUser.lastName,
    dhtUser.family_name,
    authUser.lastName
  );
  const fullName = getFirstString(
    `${firstName} ${lastName}`.trim(),
    dhtUser.name,
    dhtUser.fullName,
    authUser.name,
    authUser.username,
    'Drone Hotel User'
  );
  const email = getFirstString(
    dhtUser.GDPRUsr_Email,
    dhtUser.email,
    authUser.email
  );

  return {
    fullName,
    firstName,
    lastName,
    email,
    username: getFirstString(authUser.username, dhtUser.username, dhtUser.DHTUSRid),
    appUserId: dhtUser.DHTAPPUSR_id || dhtUser.DHTAPPUSRid || dhtUser.DHTAPPUSR_Id || dhtUser.DHTUSRid || dhtUser.DHTUSR_id || '',
    dhtUserId: dhtUser.DHTUSRid || dhtUser.DHTUSR_id || '',
    initials: getInitials(fullName, email)
  };
};

const ProfileField = ({ label, value }) => (
  <div className="profile-field">
    <span className="profile-field-label">{label}</span>
    <span className="profile-field-value">{value}</span>
  </div>
);

function ProfilePage() {
  const navigate = useNavigate();
  const profile = useMemo(buildProfile, []);
  const { language: selectedLanguage, setLanguage: setSelectedLanguage, t } = useTranslation();
  const [languageStatus, setLanguageStatus] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(localStorage.getItem('selectedOrganization') || '');
  const [selectedRole, setSelectedRole] = useState(localStorage.getItem('selectedRole') || '');
  const [selectionLoading, setSelectionLoading] = useState(true);
  const [selectionSaving, setSelectionSaving] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [selectionStatus, setSelectionStatus] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const loadUserLanguage = async () => {
      if (!profile.appUserId) {
        return;
      }

      try {
        const userLanguage = await fetchUserLanguagePreference(profile.appUserId);
        const userLanguageCode = getLanguageCodeFromPreferenceValue(userLanguage);
        if (!isCancelled && isSupportedLanguage(userLanguageCode)) {
          setSelectedLanguage(userLanguageCode);
        }
      } catch (error) {
        console.warn('Could not load user language preference:', error);
      }
    };

    loadUserLanguage();

    return () => {
      isCancelled = true;
    };
  }, [profile.appUserId, setSelectedLanguage]);

  useEffect(() => {
    let isCancelled = false;

    const loadSelections = async () => {
      try {
        if (localStorage.getItem('authSession') !== 'true') {
          throw new Error('No authentication session found. Please log in again.');
        }

        const [orgData, roleData] = await Promise.all([
          fetchGdprOrganizations(),
          fetchGdprRoles()
        ]);

        if (isCancelled) {
          return;
        }

        const normalizedOrganizations = normalizeGdprOrganizations(orgData);
        const normalizedRoles = normalizeGdprRoles(roleData);

        setOrganizations(normalizedOrganizations);
        setRoles(normalizedRoles);
        setSelectedOrg((currentValue) => currentValue || String(normalizedOrganizations[0]?.id || ''));
        setSelectedRole((currentValue) => currentValue || String(normalizedRoles[0]?.id || ''));
        setSelectionLoading(false);
      } catch (error) {
        console.warn('Could not load organizations and roles:', error);
        if (!isCancelled) {
          setSelectionError(error.message || t('Failed to load organizations and roles'));
          setSelectionLoading(false);
        }
      }
    };

    loadSelections();

    return () => {
      isCancelled = true;
    };
  }, [t]);

  const handleSelectionSubmit = async (event) => {
    event.preventDefault();
    setSelectionError('');
    setSelectionStatus('');

    if (!selectedOrg || !selectedRole) {
      setSelectionError(t('Please select both an organization and a role'));
      return;
    }

    setSelectionSaving(true);
    setSelectionStatus(t('Saving...'));

    try {
      await saveOrganizationRoleSelection({
        organizations,
        roles,
        selectedOrg,
        selectedRole
      });
      setSelectionStatus(t('Saved'));
    } catch (error) {
      console.error('Error saving profile selection:', error);
      setSelectionError(t('Failed to save your selection. Please try again.'));
      setSelectionStatus('');
    } finally {
      setSelectionSaving(false);
    }
  };

  const handleLanguageChange = async (languageCode) => {
    const language = getLanguageByCode(languageCode);

    if (!language) {
      return;
    }

    setSelectedLanguage(languageCode);
    setLanguageStatus(profile.appUserId ? t('Saving...') : t('Saved on this device'));

    if (!profile.appUserId) {
      return;
    }

    try {
      await saveUserLanguagePreference({
        userId: profile.appUserId,
        languageValue: language.value
      });
      setLanguageStatus(t('Saved'));
    } catch (error) {
      console.warn('Could not save user language preference:', error);
      setLanguageStatus(t('Saved on this device'));
    }
  };

  return (
    <div className="home-container profile-page">
      <nav className="navbar navbar-dark home-navbar shadow-sm mb-5">
        <div className="container-fluid px-4 d-flex align-items-center justify-content-center position-relative">
          <span className="navbar-brand mb-0 h1 fw-bold fs-4">Drone Hotel</span>
          <div className="d-flex gap-2 position-absolute end-0 profile-navbar-actions">
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/')}>{t('Home')}</button>
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/map')}>{t('Map')}</button>
            <button className="btn btn-outline-light rounded-pill px-4" onClick={() => navigate('/manual')}>{t('Manual')}</button>
          </div>
        </div>
      </nav>

      <main className="container profile-container">
        <section className="profile-card">
          <div className="profile-hero">
            <div className="profile-avatar" aria-hidden="true">{profile.initials}</div>
            <div>
              <p className="profile-kicker mb-2">{t('User Profile')}</p>
              <h1 className="profile-name">{profile.fullName}</h1>
              <p className="profile-email mb-0">{profile.email || profile.username || t('Account details')}</p>
            </div>
          </div>

          <div className="profile-grid">
            <ProfileField label={t('First name')} value={profile.firstName || t('Not available')} />
            <ProfileField label={t('Last name')} value={profile.lastName || t('Not available')} />
            <ProfileField label={t('Email')} value={profile.email || t('Not available')} />
            <ProfileField label={t('Username')} value={profile.username || t('Not available')} />
            <ProfileField label="DHT user ID" value={profile.dhtUserId || t('Not available')} />
          </div>

          <div className="profile-selection-section">
            {selectionStatus && <div className="profile-selection-status">{selectionStatus}</div>}

            {selectionError && <div className="alert alert-danger">{selectionError}</div>}

            <form className="profile-selection-form" onSubmit={handleSelectionSubmit}>
              <div>
                <label htmlFor="profile-org-select" className="profile-field-label">
                  {t('Organization')}
                </label>
                <select
                  id="profile-org-select"
                  className="form-select profile-selection-select"
                  value={selectedOrg}
                  onChange={(event) => setSelectedOrg(event.target.value)}
                  disabled={selectionLoading || selectionSaving || organizations.length === 0}
                >
                  <option value="">{t('-- Select an Organization --')}</option>
                  {organizations.map((org, index) => (
                    <option key={`profile-org-${org.id}-${index}`} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                {!selectionLoading && organizations.length === 0 && (
                  <small className="text-muted">{t('No organizations available')}</small>
                )}
              </div>

              <div>
                <label htmlFor="profile-role-select" className="profile-field-label">
                  {t('Role')}
                </label>
                <select
                  id="profile-role-select"
                  className="form-select profile-selection-select"
                  value={selectedRole}
                  onChange={(event) => setSelectedRole(event.target.value)}
                  disabled={selectionLoading || selectionSaving || roles.length === 0}
                >
                  <option value="">{t('-- Select a Role --')}</option>
                  {roles.map((role, index) => (
                    <option key={`profile-role-${role.id || role.name}-${index}`} value={role.id || role.name}>
                      {role.name || role.id}
                    </option>
                  ))}
                </select>
                {!selectionLoading && roles.length === 0 && (
                  <small className="text-muted">{t('No roles available')}</small>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary profile-selection-submit"
                disabled={selectionLoading || selectionSaving || !selectedOrg || !selectedRole}
              >
                {selectionSaving ? t('Saving...') : t('Save')}
              </button>
            </form>
          </div>

          <div className="profile-language-section">
            <div className="profile-language-header">
              <div>
                <span className="profile-field-label">{t('Language')}</span>
                <h2 className="profile-language-title">{t('Choose display language')}</h2>
              </div>
              <div className="profile-language-meta">
                {languageStatus && <span className="profile-language-status">{languageStatus}</span>}
                <span className="profile-language-current">{selectedLanguage}</span>
              </div>
            </div>

            <div className="profile-language-options" role="radiogroup" aria-label={t('Choose display language')}>
              {LANGUAGE_OPTIONS.map((language) => {
                const isSelected = selectedLanguage === language.code;

                return (
                  <button
                    key={language.code}
                    type="button"
                    className={`profile-language-option${isSelected ? ' is-selected' : ''}`}
                    onClick={() => handleLanguageChange(language.code)}
                    role="radio"
                    aria-checked={isSelected}
                  >
                    <span className="profile-language-code">{language.helper}</span>
                    <span className="profile-language-name">{language.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default ProfilePage;
